import { useEffect, useRef } from 'react';
import L, { type LeafletMouseEvent, type Map as LeafletMap, type Marker } from 'leaflet';
import 'leaflet/dist/leaflet.css';

type OwnerMapPickerProps = {
  latitude: number;
  longitude: number;
  onChange: (position: { latitude: number; longitude: number }) => void;
};

const markerIcon = L.icon({
  iconRetinaUrl:
    'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const OwnerMapPicker = ({
  latitude,
  longitude,
  onChange,
}: OwnerMapPickerProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<LeafletMap | null>(null);
  const markerRef = useRef<Marker | null>(null);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) {
      return;
    }

    const map = L.map(containerRef.current, {
      center: [latitude, longitude],
      zoom: 13,
      zoomControl: true,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
    }).addTo(map);

    const marker = L.marker([latitude, longitude], {
      draggable: true,
      icon: markerIcon,
    }).addTo(map);

    marker.on('dragend', () => {
      const position = marker.getLatLng();
      onChange({
        latitude: Number(position.lat.toFixed(6)),
        longitude: Number(position.lng.toFixed(6)),
      });
    });

    map.on('click', (event: LeafletMouseEvent) => {
      marker.setLatLng(event.latlng);
      onChange({
        latitude: Number(event.latlng.lat.toFixed(6)),
        longitude: Number(event.latlng.lng.toFixed(6)),
      });
    });

    mapRef.current = map;
    markerRef.current = marker;

    setTimeout(() => map.invalidateSize(), 0);

    return () => {
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const marker = markerRef.current;

    if (!map || !marker) {
      return;
    }

    const nextPosition: [number, number] = [latitude, longitude];
    marker.setLatLng(nextPosition);
    map.panTo(nextPosition, { animate: true });
  }, [latitude, longitude]);

  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
      <div ref={containerRef} className="h-[320px] w-full" />
      <div className="flex flex-col gap-2 border-t border-slate-100 px-4 py-3 text-xs text-slate-500 md:flex-row md:items-center md:justify-between">
        <span>Klik peta atau geser marker untuk menentukan titik lokasi.</span>
        <span className="font-mono text-slate-600">
          {latitude.toFixed(6)}, {longitude.toFixed(6)}
        </span>
      </div>
    </div>
  );
};

export default OwnerMapPicker;
